import React, { forwardRef, MutableRefObject, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { ContextProviderNode } from '@decisions/server/DecisionsFramework/Design/Properties';
import { Editor } from 'components/Editors/Editor';
import { IPropertyChild } from 'components/property-editors/utils/ProviderNodeUtils';
import { Callout } from 'components/layout/Callout';
import { getEditorDataAttributeProps } from 'components/Editors/utils/EditorDataAttributeHelpers';
import { PropertyGrid } from 'components/property-editors/PropertyGrid';
import { useResizeObserver } from 'hooks/useResizeObserver';
import { storeIdSelector, useMappingEditorStore } from '../MappingEditorStore';
import { updateMappingRowHeights } from '../utils/MappingUtils';

export interface IStepMappingEditorProps {
  contextNode: ContextProviderNode;
  path: string[];
  isEditorPanelOpen: boolean;
  rowRef: MutableRefObject<HTMLDivElement | null>;
  onClose: () => void;
  isOverlayType?: boolean;
  isForBuildData?: boolean;
  editingContextId?: string;
  autoFocus?: boolean;
  setFocusIn?: (focused: boolean) => void;
}

export const getOverlayClassName = (isForBuildData = false) =>
  classNames('dp-step-mapping-editor-overlay', {
    [`dp-step-mapping-editor-build-data`]: isForBuildData,
  });

export const StepMappingEditor = forwardRef(
  (
    {
      autoFocus,
      contextNode,
      editingContextId,
      setFocusIn,
      isEditorPanelOpen,
      isForBuildData,
      isOverlayType,
      onClose,
      path,
      rowRef,
    }: IStepMappingEditorProps,
    editorRef: React.ForwardedRef<HTMLDivElement>
  ) => {
    const setRowHeights = useMappingEditorStore(state => state.setStepRowHeights);
    const mappingEditorId = useMappingEditorStore(storeIdSelector);

    //We need to change some styles as per layer position.
    const getClassNameAsPerPlacement = useCallback((placement: string) => {
      if (placement === 'bottom') return 'dp-step-mapping-editor__bottom';
      else if (placement === 'top') return 'dp-step-mapping-editor__top';
    }, []);

    const { dataPath } = getEditorDataAttributeProps(contextNode as IPropertyChild, path);

    const [setObserver, observerEntry] = useResizeObserver();

    useEffect(() => {
      setObserver(rowRef.current);
      if (rowRef.current) {
        // because some editors with adjustable heights, like TextArea, will impact row height without triggering useLayoutEffect on row:
        // setRowHeight(rowRef.current?.id, rowRef.current.offsetTop, rowRef.current.offsetHeight, MappingPanelType.STEP);
        updateMappingRowHeights(mappingEditorId, setRowHeights);
      }
    }, [rowRef, setObserver, observerEntry, mappingEditorId, setRowHeights]);

    const rowRect = isForBuildData ? rowRef.current?.getBoundingClientRect() : undefined;
    const height = rowRect && Math.max(window.innerHeight - rowRect.bottom - 20, 250);

    const blockClicks = (e: React.MouseEvent) => {
      // prevent click inside editor wrapper from bubbling, so we can interact with editor without toggling the row selection / drag, etc.:
      // if (e.currentTarget.contains(e.target as HTMLElement)) {
      // e.stopPropagation();
      // }
    };

    const onEditorFocusIn = () => setFocusIn?.(true);
    const onFocusOut = () => setFocusIn?.(false);

    return isOverlayType ? (
      <Callout
        isOpen={isEditorPanelOpen}
        onClose={onClose}
        sourceRef={rowRef}
        placement={'bottom'}
        width={rowRef.current?.clientWidth && rowRef.current?.clientWidth - 20}
        getClassNameAsPerPlacement={getClassNameAsPerPlacement}
        className={getOverlayClassName(isForBuildData)}
        data-path={dataPath}
        style={{ height }}
        onClick={blockClicks}
      >
        {isForBuildData ? (
          editingContextId && (
            <PropertyGrid
              hideSearch={true}
              editingContextId={editingContextId}
              embeddedProps={{
                hideTopCategory: true,
                parentSearch: '',
              }}
            />
          )
        ) : (
          <Editor child={contextNode as IPropertyChild} path={path} autoFocus={autoFocus} />
        )}
      </Callout>
    ) : (
      <div
        className="dp-step-mapping-editor"
        onClick={blockClicks}
        onMouseDown={blockClicks}
        data-path={dataPath}
        ref={editorRef}
        // add a key propr here to 're-parent' the editor, preventing value leaks between editors of the same type in
        // a react-rendering ContextProviderNode update race condition, e.g. changing 'rename' to 'reference' DT-034916
        key={dataPath}
        onFocus={onEditorFocusIn}
        onBlur={onFocusOut}
      >
        <Editor child={contextNode as IPropertyChild} path={path} autoFocus={autoFocus} />
      </div>
    );
  }
);
